import { api } from './http'
import { and, searchParam } from './filters'

// Catálogos / parámetros de la Facturación Ágil.
// Contratos VERIFICADOS EN VIVO contra el backend MT (2026-06-25).

// --- Tipo de comprobante default ágil ---
// VIVO: GET /tipocomprobantes?view=ConsultaComprobanteVentaForm&search=<and(...)> -> 1 item rico
//   { id, codigo:'FK', clase:{codigo:'factura'}, condicion:{codigo:'tipo_comprobante_contado'}, ... }
// El filtro real (form.js:2305-2322) es AND de:
//   utilizarEnProcesosVenta==true, clase.codigo=='factura', factAgilDefault==true, activo==true.
// NO es ?factAgilDefault=true (ese flag no filtra; el item viene sin claves útiles sin view).
// Selección: el tipo con condicion.codigo=='tipo_comprobante_contado', si no, el primero.
export function getTiposComprobanteAgil() {
  const filtro = and([
    { path: 'utilizarEnProcesosVenta', equals: true },
    { path: 'clase.codigo', equals: 'factura' },
    { path: 'factAgilDefault', equals: true },
    { path: 'activo', equals: true },
  ])
  return api
    .get('/tipocomprobantes', {
      params: searchParam(filtro, { view: 'ConsultaComprobanteVentaForm' }),
    })
    .then((r) => r.data)
}

// Helper de selección del tipo default a partir de la lista (contado preferido).
export function pickTipoDefault(tipos) {
  if (!Array.isArray(tipos) || tipos.length === 0) return null
  return tipos.find((t) => t?.condicion?.codigo === 'tipo_comprobante_contado') || tipos[0]
}

// --- Timbrado ágil ---
// VIVO: GET /timbradoempresapuntoemision?view=ConsultaComprobanteVentaForm&search=<and(...)> -> 1 item
//   { id:51, puntoEmisionVenta:{ id, numero, caja:{id, nombre} }, timbradoEmpresa:{ numeroTimbrado,
//     fechaDesde, fechaHasta, claseTimbrado:{codigo:'timbrado_autoimpreso'}, factAgilDefault:true },
//     numeroInicial, numeroFinal, numeroActual }
// El filtro REAL (recuperarTimbradoFacturacionAgil, form.js:9076) necesita la CAJA del usuario:
//   puntoEmisionVenta.id == caja.puntoEmision.id, timbradoEmpresa.factAgilDefault==true,
//   timbradoEmpresa.activo==true, tipoComprobante.codigo == tipo.clase.codigo ('factura').
// La caja viene de loginApp -> accesoSistema.cajas[] (ver auth.js / useFacturacionAgil).
// claseTimbrado.codigo decide SIFEN: 'timbrado_virtual' => generarJsonVirtual; en este
// tenant es 'timbrado_autoimpreso' => impresión directa, sin SIFEN.
export function getTimbradoAgil({ puntoEmisionId, claseComprobanteCodigo = 'factura' } = {}) {
  const filtros = [
    { path: 'timbradoEmpresa.factAgilDefault', equals: true },
    { path: 'timbradoEmpresa.activo', equals: true },
    { path: 'tipoComprobante.codigo', equals: claseComprobanteCodigo },
  ]
  if (puntoEmisionId != null) {
    filtros.unshift({ path: 'puntoEmisionVenta.id', equals: puntoEmisionId })
  }
  return api
    .get('/timbradoempresapuntoemision', {
      params: searchParam(and(filtros), { view: 'ConsultaComprobanteVentaForm' }),
    })
    .then((r) => r.data)
    .then((list) => (Array.isArray(list) ? list[0] || null : list))
}

// --- Listas de precio de venta ---
// VIVO: GET /listaprecioventa -> array(4). Para filtrar por estado/cliente el front usa
// search=and(estado.codigo=='lista_activada' [, cliente.id==...]). // PENDIENTE: aplicar
// filtro por cliente si el flujo lo requiere (recuperarListaPrecio, form.js:9101+).
export function getListasPrecioVenta(params = {}) {
  return api.get('/listaprecioventa', { params }).then((r) => r.data)
}

// --- Cotización del día ---
// VIVO: GET /cotizacionmonedas?view=BaseList -> array
//   [{ id, monedaDesde:{codigo}, monedaHasta:{codigo}, fecha:'YYYY/MM/DD', valorCompra, valorVenta }]
// Para el cambio: tomar la fila monedaDesde.codigo == moneda extranjera (ej. 'dolares'),
// monedaHasta.codigo == 'guaranies', con la fecha más reciente.
export function getCotizaciones(params = {}) {
  return api.get('/cotizacionmonedas', { params: { view: 'BaseList', ...params } }).then((r) => r.data)
}

// --- Monedas ---
// VIVO: GET /tipos/tipo/monedas -> array(5) [{ id, codigo, descripcion, tipoTipo }].
export function getMonedas() {
  return api.get('/tipos/tipo/monedas').then((r) => r.data)
}

// --- Tipos de transacción ---
// VIVO: GET /tipos/tipo/tipo_transaccion -> [{id, codigo}] con codigos:
//   transaccion_anticipo, mixto, prestacion_servicios, venta_activo_fijo, venta_mercaderia.
// El comprobante ágil usa por default el de codigo 'venta_mercaderia' (form.js:2253).
export function getTiposTransaccion() {
  return api.get('/tipos/tipo/tipo_transaccion').then((r) => r.data)
}

// --- Formas de pago (para cobros) ---
// VIVO: GET /formapago?view=BaseList&utilizarEnCobros=true -> array(6) ricos
//   [{ id, descripcion:'Efectivo', clase:{codigo:'forma_pago_efectivo'}, depositable, ... }]
export function getFormasPago() {
  return api.get('/formapago', { params: { view: 'BaseList', utilizarEnCobros: true } }).then((r) => r.data)
}

// --- Parámetros del sistema ---
// El factory Angular usa ParametrosFactory.getByCodigo -> filtro search por codigo.
// // PENDIENTE: confirmar param exacto en vivo si se usa (probablemente search=and(codigo==...)).
export function getParametro(codigo) {
  return api
    .get('/parametros', { params: searchParam(and([{ path: 'codigo', equals: codigo }])) })
    .then((r) => r.data)
}

// --- Caja por id (detalle) ---
// El factory Angular usa GET /cajas/c/{id}?view=CajaList (cajaFactory.getCaja, form.js:5055).
export function getCaja(id, view = 'CajaList') {
  return api.get(`/cajas/c/${id}`, { params: { view } }).then((r) => r.data)
}

// --- Almacenes / depósitos ---
// VIVO: GET /almacenes?view=BaseList -> array(14) [{ id, descripcion, modificarPrecioEspecial, ... }].
// OBLIGATORIO en el payload: sin deposito, el backend NPE-ea en validarYAsignarListaPrecios
// (getDeposito().getModificarPrecioEspecial() sin null-check) cuando el cliente tiene lista.
export function getAlmacenes() {
  return api.get('/almacenes', { params: { view: 'BaseList' } }).then((r) => r.data)
}

// --- Catálogos del flujo de cobro (VIVO 2026-06-25) ---
// tipoCobro: GET /tipos/tipo/tipo_cobro?view=BaseForm -> usar codigo 'tipo_cobro_normal' (id 7483).
export function getTiposCobro() {
  return api.get('/tipos/tipo/tipo_cobro', { params: { view: 'BaseForm' } }).then((r) => r.data)
}
// estado proceso cobro: GET /tipos/tipo/estado_procesocobro -> 'estado_procesocobro_abierto' (id 383).
export function getEstadosProcesoCobro() {
  return api.get('/tipos/tipo/estado_procesocobro').then((r) => r.data)
}
// tipoCobroEntidad: GET /tipocobro?view=BaseList -> [ANTICIPO, CONTADO, CREDITO]; usar 'Contado' (id 1).
export function getTiposCobroEntidad() {
  return api.get('/tipocobro', { params: { view: 'BaseList' } }).then((r) => r.data)
}
